// Task 6 — /admin/teams API client. Paths WITHOUT the leading "/api" (the shared client prefixes it).
//   GET    /admin/teams              → { teams: TeamRecord[] }
//   POST   /admin/teams              → { team: TeamRecord }
//   PATCH  /admin/teams/:id          → { team: TeamRecord }
//   POST   /admin/teams/:id/archive  → { archived: boolean }
//   PUT    /admin/teams/:id/members/:userId → { assigned: boolean }
//   DELETE /admin/teams/members/:userId → { removed: boolean }
//   GET    /admin/teams/roster       → { users: TenantUser[] } (T2 — team.manage-gated)
//   GET    /admin/teams/:id/delegations → { delegations: DelegationRecord[] } (T4)
//   POST   /admin/teams/:id/delegations → { delegation: DelegationRecord } (T4)
//   DELETE /admin/teams/delegations/:delegationId → { revoked: boolean } (T4)
import { api } from "@/lib/api-client";

/** Row shape of GET /admin/teams/roster (team.manage-gated) — the shared
 * picker source that TeamDialog's lead select and MembersPanel's roster
 * both consume. `teamId` moved here in T2 when it was stripped from the
 * open GET /users endpoint (T1 trade-off closed). */
export interface TenantUser {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
  teamId: string | null;
}

export interface TeamRecord {
  id: string;
  tenantId: string;
  name: string;
  parentTeamId: string | null;
  supervisorUserId: string;
  supervisorName: string | null;
  /** False when the lead user is deactivated — Lead cell shows a warning
   * badge (delegation is the remedy, spec §8). */
  supervisorIsActive: boolean;
  memberCount: number;
  createdAt: string;
  archivedAt: string | null;
  /** T4 — count of currently-active (window contains now) delegations for this team. */
  activeDelegationCount: number;
}

/** T4 — a supervisor delegation window on a team. Mirrors the api's
 * team-delegation-service row shape; ISO strings for all dates. */
export interface DelegationRecord {
  id: string;
  teamId: string;
  toUserId: string;
  toUserName: string | null;
  startsAt: string;
  endsAt: string;
  reason: string;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
  revokedBy: string | null;
  revokedAt: string | null;
}

export interface CreateDelegationPayload {
  toUserId: string;
  startsAt: string;
  endsAt: string;
  reason: string;
}

export interface CreateTeamPayload {
  name: string;
  parentTeamId?: string;
  supervisorUserId: string;
}

export interface UpdateTeamPayload {
  name?: string;
  parentTeamId?: string | null;
  supervisorUserId?: string;
}

export const teamsApi = {
  list: () => api.get<{ teams: TeamRecord[] }>("/admin/teams"),
  roster: () => api.get<{ users: TenantUser[] }>("/admin/teams/roster"),
  create: (payload: CreateTeamPayload) =>
    api.post<{ team: TeamRecord }>("/admin/teams", payload),
  update: (id: string, payload: UpdateTeamPayload) =>
    api.patch<{ team: TeamRecord }>(`/admin/teams/${id}`, payload),
  archive: (id: string) =>
    api.post<{ archived: boolean }>(`/admin/teams/${id}/archive`, {}),
  assignMember: (teamId: string, userId: string) =>
    api.put<{ assigned: boolean }>(`/admin/teams/${teamId}/members/${userId}`, {}),
  removeMember: (userId: string) =>
    api.delete<{ removed: boolean }>(`/admin/teams/members/${userId}`),
  listDelegations: (teamId: string) =>
    api.get<{ delegations: DelegationRecord[] }>(`/admin/teams/${teamId}/delegations`),
  createDelegation: (teamId: string, payload: CreateDelegationPayload) =>
    api.post<{ delegation: DelegationRecord }>(`/admin/teams/${teamId}/delegations`, payload),
  revokeDelegation: (delegationId: string) =>
    api.delete<{ revoked: boolean }>(`/admin/teams/delegations/${delegationId}`),
};
